import { images } from '../constants/images';
import { Page } from './pages-types';
import { PageRoutes } from './page-routes';
import { tents } from '../constants/tents';
import { TentTypeV1 } from '@camp67/model';
import { MessageKey } from '../locales/en';
import { FormattedString, useMessages } from '../i18n/useMessages';
import { Link } from '@remix-run/react';
import { displayPrice } from '../state/prices';
import { getPrice } from '../state/get-tent-price';
import { arePricesHidden } from '../state/inquiry-modal';
import React from 'react';

export type ChildPage = Pick<Page, 'path' | 'readingTime' | 'title' | 'summary'> & {
  heroImage?: string;
};

const tentMessageKeyFromTentType = (tentType: TentTypeV1): MessageKey => {
  switch (tentType) {
    case TentTypeV1.TWIN_ROOM: {
      return 'camp67-navigation.packages.twin-room';
    }
    case TentTypeV1.FAMILY_ROOM: {
      return 'camp67-navigation.packages.family-room';
    }
    case TentTypeV1.DORM_SINGLE: {
      return 'camp67-navigation.packages.dormitory-single';
    }
    case TentTypeV1.PRIVATE_GROUP_COTTAGE: {
      return 'camp67-navigation.packages.private-group-cottage';
    }
    case TentTypeV1.DORM_BUNK: {
      return 'camp67-navigation.packages.dormitory-bunk';
    }
  }
};

export const tentSummaryFromTentType = (tentType: TentTypeV1) => {
  switch (tentType) {
    case TentTypeV1.TWIN_ROOM: {
      return (
        <FormattedString
          message="camp67-tent-page.twin-room-summary"
          components={{ paragraph: <p className="mt-3" /> }}
        />
      );
    }
    case TentTypeV1.FAMILY_ROOM: {
      return (
        <FormattedString
          message="camp67-tent-page.family-room-summary"
          components={{ paragraph: <p className="mt-3" /> }}
        />
      );
    }
    case TentTypeV1.DORM_SINGLE: {
      return <FormattedString message="camp67-tent-page.dorm-single-summary" />;
    }
    case TentTypeV1.PRIVATE_GROUP_COTTAGE: {
      return <FormattedString message="camp67-tent-page.private-group-cottage-summary" />;
    }
    case TentTypeV1.DORM_BUNK: {
      return (
        <FormattedString
          message="camp67-tent-page.dorm-bunk-summary"
          components={{ paragraph: <p className="mt-3" /> }}
        />
      );
    }
  }
};

// Package preview card component
interface PackagePreviewProps {
  path: string;
  readingTime: number;
  title: MessageKey;
  subtitle?: MessageKey;
  heroImage: string;
  price?: string;
  capacity?: number;
}

const PackagePreview: React.FC<PackagePreviewProps> = ({
  path,
  readingTime,
  title,
  subtitle,
  heroImage,
  price,
  capacity,
}) => {
  const getMessage = useMessages();
  const titleText = getMessage(title);
  const subtitleText = subtitle ? getMessage(subtitle) : undefined;

  return (
    <article className="group">
      <Link prefetch="intent" to={path}>
        <div className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-orange-200">
          <div className="relative h-56 overflow-hidden">
            <img
              src={heroImage}
              alt={titleText}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
              <span className="inline-block px-2.5 py-1 bg-black/50 backdrop-blur-sm text-white text-xs font-medium uppercase tracking-wider rounded-full">
                {readingTime} min read
              </span>
              {!arePricesHidden && price && (
                <span className="inline-block px-2.5 py-1 bg-white/90 text-gray-800 text-xs font-bold rounded-full">
                  {price}/night
                </span>
              )}
            </div>
          </div>
          <div className="p-6">
            <h3 className="font-serif text-gray-900 group-hover:text-orange-600 text-xl font-bold transition-colors">
              {titleText}
            </h3>
            {subtitleText && (
              <p className="mt-2 text-gray-600 text-sm line-clamp-2">{subtitleText}</p>
            )}
            {capacity && (
              <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                <span>
                  Up to {capacity} guest{capacity !== 1 ? 's' : ''}
                </span>
              </div>
            )}
            <div className="mt-5 flex items-center text-orange-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
              View Details &rarr;
            </div>
          </div>
        </div>
      </Link>
    </article>
  );
};

// Build the packages page children with accommodation data
export const packagesPageChildren: ChildPage[] = [
  {
    title: 'camp67-navigation.packages.reserve-a-room',
    readingTime: 0,
    path: PageRoutes.reservation,
    summary: <FormattedString message="camp67-packages-page-children.summary" />,
    heroImage: images['twin-tent-v4-1'],
  },
  ...Object.values(tents).map(
    (tent): ChildPage => ({
      path: tent.path,
      readingTime: 2,
      title: tentMessageKeyFromTentType(tent.tentType),
      summary: tentSummaryFromTentType(tent.tentType),
      heroImage: tent.imageUrl,
    }),
  ),
];

// Accommodation cards for the packages overview
const AccommodationCards: React.FC = () => {
  const tentList = Object.values(tents);

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {tentList.map((tent, index) => (
        <PackagePreview
          key={index}
          path={tent.path}
          readingTime={2}
          title={tentMessageKeyFromTentType(tent.tentType)}
          subtitle={tent.subtitle}
          heroImage={tent.imageUrl}
          price={displayPrice({
            priceInINR: getPrice(tent.tentType),
            showCurrency: true,
          })}
          capacity={tent.people}
        />
      ))}
    </div>
  );
};

export const packagesPage: Page = {
  title: 'camp67-navigation.packages',
  subtitle: 'camp67-packages-page.subtitle',
  summary: <FormattedString message="camp67-packages-page.summary" />,
  path: PageRoutes.packages,
  heroImage: images['who-are-we-hero-v2'],
  heroImageAlt: 'camp67-packages-page.hero-image-alt',
  readingTime: packagesPageChildren.reduce(
    (acc, { readingTime }) => acc + readingTime,
    0,
  ),
  maxWidth: true,
  content: <AccommodationCards />,
};
